import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import { Check, Lock, Smartphone, Sparkles, Zap } from 'lucide-react'
import { cardTemplates } from '../../templates/cardTemplates'

export default function Themes() {
  const { currentUser, userProfile } = useAuth()
  const [currentTemplate, setCurrentTemplate] = useState('simple-identity')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    async function fetchTemplate() {
      if (!currentUser) return
      try {
        const vcardDoc = await getDoc(doc(db, 'vcards', currentUser.uid))
        if (vcardDoc.exists()) {
          setCurrentTemplate(vcardDoc.data().template || 'simple-identity')
        }
      } catch (error) {
        console.error('Error fetching template:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchTemplate()
  }, [currentUser])

  async function selectTemplate(templateId) {
    const template = cardTemplates.find(t => t.id === templateId)
    
    if (!template?.free && userProfile?.plan === 'free') {
      alert('Upgrade to Pro to unlock premium templates!')
      return
    }

    setSaving(true)
    try {
      await updateDoc(doc(db, 'vcards', currentUser.uid), {
        template: templateId
      })
      setCurrentTemplate(templateId)
    } catch (error) {
      console.error('Error updating template:', error)
      alert('Failed to save template. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '60vh' }}>
        <div className="spinner"></div>
      </div>
    )
  }

  // Filter templates
  const freeTemplates = cardTemplates.filter(t => t.free)
  const proTemplates = cardTemplates.filter(t => !t.free)

  return (
    <div className="themes-page">
      <div className="header-section">
        <div className="header-content">
          <h1 className="page-title">
            <Smartphone className="icon" />
            Designed to Impress
          </h1>
          <p className="page-subtitle">Choose a professionally crafted template for your digital business card.</p>
        </div>
      </div>

      <div className="templates-container">
        {/* Free Collection */}
        <section className="collection-section">
          <div className="section-header">
            <h2 className="section-title">
              <Zap className="icon" size={20} />
              Starter Collection
            </h2>
            <span className="badge free">Free</span>
          </div>
          
          <div className="mockup-grid">
            {freeTemplates.map((template) => (
              <PhoneMockup
                key={template.id}
                template={template}
                isActive={currentTemplate === template.id}
                isLocked={false}
                onSelect={() => !saving && selectTemplate(template.id)}
              />
            ))}
          </div>
        </section>

        {/* Pro Collection */}
        <section className="collection-section pro-section">
          <div className="section-header">
            <h2 className="section-title">
              <Sparkles className="icon" size={20} />
              Premium Collection
            </h2>
            {userProfile?.plan === 'free' ? (
              <span className="badge pro-locked">Pro Locked</span>
            ) : (
              <span className="badge pro-unlocked">Pro Unlocked</span>
            )}
          </div>
          
          <div className="mockup-grid">
            {proTemplates.map((template) => (
              <PhoneMockup
                key={template.id}
                template={template}
                isActive={currentTemplate === template.id}
                isLocked={userProfile?.plan === 'free'}
                onSelect={() => !saving && selectTemplate(template.id)}
              />
            ))}
          </div>
        </section>
      </div>

      <style>{`
        .themes-page {
          max-width: 1400px;
          margin: 0 auto;
          padding-bottom: 4rem;
        }

        .header-section {
          text-align: center;
          margin-bottom: 3rem;
          padding: 2rem 0;
        }

        .page-title {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--text-heading);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 0.5rem;
        }

        .page-title .icon {
          color: var(--primary);
        }

        .page-subtitle {
          font-size: 1.1rem;
          color: var(--text-muted);
          max-width: 600px;
          margin: 0 auto;
        }

        .collection-section {
          margin-bottom: 4rem;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
          padding-left: 1rem;
          border-left: 4px solid var(--primary);
        }

        .section-title {
          font-size: 1.5rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 0;
        }

        .section-title .icon {
          color: var(--text-body);
        }

        .pro-section .section-title .icon {
          color: #f59e0b;
        }

        .badge {
          font-size: 0.75rem;
          font-weight: 700;
          padding: 4px 12px;
          border-radius: 999px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .badge.free { background: #e0f2fe; color: #0284c7; }
        .badge.pro-locked { background: #1e293b; color: #fbbf24; }
        .badge.pro-unlocked { background: #fef3c7; color: #d97706; }

        .mockup-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, 340px);
          gap: 3rem;
          justify-content: center;
        }

        /* PHONE MOCKUP STYLES */
        .phone-mockup-container {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.5rem;
          group: 1; /* For hover effects */
        }

        /* The Phone Device */
        .phone-device {
          width: 300px;
          height: 600px; /* 1:2 visual ratio, scaled from 420x750 */
          background: #1a1a1a;
          border-radius: 40px;
          padding: 12px; /* Bezel thickness */
          box-shadow: 
            0 20px 40px -10px rgba(0,0,0,0.2), 
            0 0 0 1px rgba(0,0,0,0.1);
          position: relative;
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Clickable Area */
        .phone-mockup-container:hover .phone-device {
          transform: translateY(-10px);
          box-shadow: 
            0 30px 60px -15px rgba(0,0,0,0.3),
            0 0 0 4px var(--primary-glow, rgba(59, 130, 246, 0.5));
        }

        .phone-device.active {
          box-shadow: 
            0 25px 50px -12px rgba(16, 185, 129, 0.25),
            0 0 0 4px #10b981;
          border-color: #10b981;
        }

        /* Screen Area */
        .phone-screen {
          width: 100%;
          height: 100%;
          background: #fff;
          border-radius: 28px;
          overflow: hidden;
          position: relative;
        }

        .template-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: top center;
        }

        /* Notion Island / Camera */
        .phone-notch {
          position: absolute;
          top: 12px;
          left: 50%;
          transform: translateX(-50%);
          width: 80px;
          height: 24px;
          background: #1a1a1a;
          border-bottom-left-radius: 12px;
          border-bottom-right-radius: 12px;
          z-index: 20;
        }

        /* Overlay on Hover */
        .phone-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(2px);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: 10;
        }

        .phone-mockup-container:hover .phone-overlay {
          opacity: 1;
        }

        .action-btn {
          padding: 12px 28px;
          border-radius: 99px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transform: translateY(20px);
          transition: all 0.3s ease;
        }

        .phone-mockup-container:hover .action-btn {
          transform: translateY(0);
        }

        .btn-select { background: white; color: black; }
        .btn-select:hover { background: var(--primary); color: white; }
        
        .btn-active { background: #10b981; color: white; cursor: default; }

        .btn-locked { background: rgba(255,255,255,0.2); color: white; backdrop-filter: blur(4px); display: flex; align-items: center; gap: 8px; }

        /* Template Name Below */
        .template-info {
          text-align: center;
        }

        .template-name {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text-heading);
          margin-bottom: 4px;
        }

        .active-indicator {
          color: #10b981;
          font-size: 0.85rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        @media (max-width: 768px) {
          .mockup-grid {
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 2rem;
          }
          
          .phone-device {
            width: 260px;
            height: 520px;
          }
        }
      `}</style>
    </div>
  )
}

function PhoneMockup({ template, isActive, isLocked, onSelect }) {
  return (
    <div className="phone-mockup-container" onClick={(!isLocked && !isActive) ? onSelect : undefined}>
      <div className={`phone-device ${isActive ? 'active' : ''}`}>
        <div className="phone-notch"></div>
        <div className="phone-screen">
          <img 
            src={template.coverImage} 
            alt={template.name} 
            className="template-image"
            loading="lazy"
          />
          
          <div className="phone-overlay">
            {isActive ? (
              <button className="action-btn btn-active">
                 Selected
              </button>
            ) : isLocked ? (
              <button className="action-btn btn-locked">
                <Lock size={16} /> Pro Only
              </button>
            ) : (
              <button className="action-btn btn-select">
                Use Template
              </button>
            )}
          </div>
        </div>
      </div>
      
      <div className="template-info">
        <div className="template-name">{template.name}</div>
        {isActive && (
          <div className="active-indicator">
            <Check size={14} /> Currently Active
          </div>
        )}
      </div>
    </div>
  )
}